import React, { useEffect, useState, useMemo } from 'react';
import '../App.css';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import PlanSummaryBox from '../components/SummaryBoxTestPlans';
import TestPlansAccordion from '../components/TestPlanAccordion';
import { Spinner } from 'react-bootstrap';
import {
  useGenerateTestPlansByCodestream,
  useFetchPlansByCodestream,
} from '../hooks/useServices';

const CODE_STREAMS = [
  { label: 'SLE16', codestream: 'sle16' },
  { label: 'SLE15 SP7', codestream: 'sle15-SP7' },
];

export default function TestPlansPage() {
  const [selectedCodestream, setSelectedCodestream] = useState(CODE_STREAMS[0]);
  const [selectedSquads, setSelectedSquads] = useState([]);

  const {
    plans: structuredPlans,
    loading,
    fetchPlans,
    clearPlans,
  } = useGenerateTestPlansByCodestream();

  const {
    plans: rawPlans,
    fetchPlans: fetchRawPlans,
    clearPlans: clearRawPlans,
  } = useFetchPlansByCodestream();

  useEffect(() => {
    fetchPlans(selectedCodestream.codestream);
    fetchRawPlans(selectedCodestream.codestream);
  }, [selectedCodestream]);

  const handleCodeStreamClick = (cs) => {
    setSelectedCodestream(cs);
    clearPlans();
    clearRawPlans();
    fetchPlans(cs.codestream);
    fetchRawPlans(cs.codestream);
  };

  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchFromNav = (searchText, context) => {
    if (context === 'plans') {
      setSearchQuery(searchText);
    }
  };

  const allSquads = useMemo(() => {
    const squadSet = new Set();
    Object.values(structuredPlans)
      .flat()
      .forEach((t) => {
        if (t.squad) squadSet.add(t.squad);
      });
    return Array.from(squadSet).sort();
  }, [structuredPlans]);

  const filteredPlans = useMemo(() => {
    if (selectedSquads.length === 0) return structuredPlans;
    const result = {};
    for (const [arch, tests] of Object.entries(structuredPlans)) {
      const filteredTests = tests.filter((t) =>
        selectedSquads.includes(t.squad)
      );
      if (filteredTests.length > 0) result[arch] = filteredTests;
    }
    return result;
  }, [structuredPlans, selectedSquads]);

  const grouped = Object.entries(filteredPlans).reduce((acc, [arch, tests]) => {
    acc[arch] = tests.reduce((squadAcc, test) => {
      const squad = test.squad || 'unknown';
      squadAcc[squad] = squadAcc[squad] || [];
      squadAcc[squad].push(test);
      return squadAcc;
    }, {});
    return acc;
  }, {});

  const handleSquadToggle = (squad) => {
    setSelectedSquads((prev) =>
      prev.includes(squad) ? prev.filter((s) => s !== squad) : [...prev, squad]
    );
  };

  return (
    <div className="testplans-wrapper">
      <Navigation onSearch={handleSearchFromNav} />
      <div className="container-fluid custom-container">
        <div className="row">
          {/* Left Panel */}
          <div className="col-md-2 section-left">
            <h4>Code Streams</h4>
            {CODE_STREAMS.map((cs) => (
              <button
                key={cs.label}
                className={`btn btn-success mb-2 w-100 ${
                  selectedCodestream.label === cs.label ? 'active-stream' : ''
                }`}
                onClick={() => handleCodeStreamClick(cs)}
                disabled={loading}
              >
                {cs.label}
              </button>
            ))}
          </div>

          {/* Center Panel */}
          <div className="col-md-8 tests-list-container">
            {searchQuery && (
              <div
                className="position-absolute text-danger"
                style={{ top: '70px', right: '16.6667%', fontSize: '0.9rem' }}
              >
                <strong>Search for TestPlans TO BE IMPLEMENTED</strong>
              </div>
            )}
            <PlanSummaryBox
              title={`Test Plans for ${selectedCodestream.label}`}
              loading={loading}
              plans={filteredPlans}
              rawPlans={
                selectedSquads.length === 0
                  ? rawPlans
                  : rawPlans.filter((plan) =>
                      selectedSquads.includes(plan.squad)
                    )
              }
              mode="codestream"
              onSquadClick={handleSquadToggle}
              selectedSquads={selectedSquads}
            />

            <div className="mb-3">
              <strong>Filter by Squad:</strong>
              {allSquads.map((squad) => (
                <div key={squad} className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`squad-${squad}`}
                    value={squad}
                    checked={selectedSquads.includes(squad)}
                    onChange={() => handleSquadToggle(squad)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`squad-${squad}`}
                  >
                    {squad}
                  </label>
                </div>
              ))}
            </div>

            {loading ? (
              <div className="text-center my-3">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <TestPlansAccordion groupedPlans={grouped} />
            )}
          </div>

          {/* Right Panel */}
          <div className="col-md-2 section-right"></div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
